import { CreateBoundedContextHandler } from '../../bounded-contexts/strategic-design/application/handlers/CreateBoundedContextHandler';
import { CreateBoundedContextCommand } from '../../bounded-contexts/strategic-design/application/commands/CreateBoundedContextCommand';
import { GetBoundedContextHandler } from '../../bounded-contexts/strategic-design/application/handlers/GetBoundedContextHandler';
import { ListBoundedContextsHandler } from '../../bounded-contexts/strategic-design/application/handlers/ListBoundedContextsHandler';
import { ListBoundedContextsQuery, GetBoundedContextQuery } from '../../bounded-contexts/strategic-design/application/queries/GetBoundedContextQuery';
import { CreateBoundedContextRequest, GetBoundedContextsQuery } from '../../bounded-contexts/strategic-design/interface/dto/BoundedContextRequestDTOs';
import { BoundedContextViewModel, BoundedContextSummaryViewModel, ContextMapViewModel } from '../../bounded-contexts/strategic-design/interface/view-models/BoundedContextViewModel';

/**
 * Bounded Context Application Service
 * 
 * Orchestrates bounded context operations and provides the boundary between
 * the API layer and the domain/application layers.
 * 
 * This is the ONLY allowed binding point for bounded context API controllers.
 * 
 * FIXED: No more Inversify - direct constructor injection following party-manager pattern.
 */
export class BoundedContextApplicationService {
    constructor(
        private readonly createBoundedContextHandler: CreateBoundedContextHandler,
        private readonly getBoundedContextHandler: GetBoundedContextHandler,
        private readonly listBoundedContextsHandler: ListBoundedContextsHandler
    ) {}

    /**
     * Create a new bounded context
     */
    async createBoundedContext(request: CreateBoundedContextRequest): Promise<BoundedContextViewModel> {
        const command = new CreateBoundedContextCommand(
            request.domainId,
            {
                name: request.name,
                description: request.description,
                type: 'core'
            },
            'api-session',
            'api-user'
        );

        const result = await this.createBoundedContextHandler.handle(command);
        
        return this.mapToBoundedContextViewModel(result);
    }

    /**
     * List bounded contexts for a domain (used by MCP server)
     */
    async listBoundedContexts(domainId?: string): Promise<BoundedContextSummaryViewModel[]> {
        const query: GetBoundedContextsQuery = {
            domainId,
            status: 'production' as const,
            offset: 0,
            limit: 100,
            search: undefined,
            includeAggregates: false
        };
        return this.getBoundedContexts(query);
    }

    /**
     * Get all bounded contexts with optional filtering
     */
    async getBoundedContexts(query: GetBoundedContextsQuery): Promise<BoundedContextSummaryViewModel[]> {
        const listQuery = new ListBoundedContextsQuery(
            {
                domainId: query.domainId,
                page: Math.floor(query.offset / query.limit) + 1,
                pageSize: query.limit,
                searchTerm: query.search
            },
            'api-session',
            'api-user'
        );

        const result = await this.listBoundedContextsHandler.handle(listQuery);
        
        return result.items.map((context: any) => ({
            id: context.id,
            name: context.name,
            domainName: context.domainName || 'Unknown Domain',
            status: this.mapContextStatus(context.status),
            aggregateCount: context.aggregateCount || 0,
            complexity: this.calculateComplexity(context),
            lastUpdated: context.updatedAt || context.createdAt
        }));
    }

    /**
     * Get a specific bounded context by ID
     */
    async getBoundedContext(contextId: string): Promise<BoundedContextViewModel> {
        const query = new GetBoundedContextQuery('unknown-domain', contextId, 'api-session', 'api-user');
        const result = await this.getBoundedContextHandler.handle(query);
        
        return this.mapToBoundedContextViewModel(result);
    }

    /**
     * Get context map showing relationships between bounded contexts
     */
    async getContextMap(domainId?: string): Promise<ContextMapViewModel> {
        const listQuery = new ListBoundedContextsQuery(
            {
                domainId,
                page: 1,
                pageSize: 1000
            },
            'api-session',
            'api-user'
        );

        const result = await this.listBoundedContextsHandler.handle(listQuery);
        
        const contexts: BoundedContextSummaryViewModel[] = result.items.map((context: any) => ({
            id: context.id,
            name: context.name,
            domainName: context.domainName || 'Unknown Domain',
            status: this.mapContextStatus(context.status),
            aggregateCount: context.aggregateCount || 0,
            complexity: this.calculateComplexity(context),
            lastUpdated: context.updatedAt || context.createdAt
        }));

        // Group contexts by domain
        const domainGroups = contexts.reduce((groups, context) => {
            const domainName = context.domainName;
            if (!groups[domainName]) {
                groups[domainName] = {
                    id: domainId || 'unknown',
                    name: domainName,
                    contextIds: []
                };
            }
            groups[domainName].contextIds.push(context.id);
            return groups;
        }, {} as Record<string, any>);

        // For now, create simplified relationships (would be enhanced with actual relationship data)
        const relationships = contexts.slice(0, -1).map((context, index) => ({
            id: `rel_${index}`,
            upstreamContext: context.id,
            downstreamContext: contexts[index + 1].id,
            relationshipType: 'customer_supplier' as const,
            description: `${context.name} provides data to ${contexts[index + 1].name}`
        }));

        return {
            contexts,
            relationships,
            domains: Object.values(domainGroups)
        };
    }

    /**
     * Map internal bounded context result to View Model
     */
    private mapToBoundedContextViewModel(result: any): BoundedContextViewModel {
        return {
            id: result.id || result.boundedContextId,
            name: result.name,
            description: result.description,
            purpose: result.purpose || result.description,
            domainId: result.domainId,
            domainName: result.domainName || 'Unknown Domain',
            status: this.mapContextStatus(result.status),
            createdAt: result.createdAt || new Date().toISOString(),
            updatedAt: result.updatedAt || result.createdAt || new Date().toISOString(),
            aggregates: {
                total: result.aggregateCount || 0,
                designed: result.designedAggregates || 0,
                entities: result.entityCount || 0,
                valueObjects: result.valueObjectCount || 0
            },
            integrations: {
                upstream: result.upstreamContexts || [],
                downstream: result.downstreamContexts || [],
                sharedKernel: result.sharedKernelContexts || []
            },
            teamResponsibility: {
                primaryTeam: result.primaryTeam,
                collaboratingTeams: result.collaboratingTeams || []
            },
            complexity: this.calculateComplexity(result)
        };
    }

    /**
     * Map bounded context status from internal to external representation
     */
    private mapContextStatus(internalStatus: string | undefined): 'planned' | 'development' | 'production' | 'deprecated' {
        switch (internalStatus) {
            case 'PLANNED': return 'planned';
            case 'DEVELOPMENT': return 'development';
            case 'PRODUCTION': return 'production';
            case 'DEPRECATED': return 'deprecated';
            default: return 'planned';
        }
    }

    /**
     * Calculate bounded context complexity
     */
    private calculateComplexity(context: any): 'low' | 'medium' | 'high' | 'very_high' {
        const aggregates = context.aggregateCount || 0;
        const entities = context.entityCount || 0;
        const valueObjects = context.valueObjectCount || 0;
        const integrations = (context.upstreamContexts?.length || 0) + (context.downstreamContexts?.length || 0);
        
        const complexityScore = aggregates * 3 + entities * 2 + valueObjects + integrations * 2;
        
        if (complexityScore < 5) return 'low';
        if (complexityScore < 15) return 'medium';
        if (complexityScore < 30) return 'high';
        return 'very_high';
    }
}